import axios from "axios";
import { WIDGET_ENDPOINTS, API_BASE_URL } from "../constants/apis";
import tokenRefreshService from "./tokenRefresh";

// Get bot configuration from API
export const getBotConfiguration = async () => {
    try {
        const token = await tokenRefreshService.getValidToken();
        console.log('Using token:', token ? 'Valid token obtained' : 'No token');

        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
        const response = await axios.get(`${API_BASE_URL}${WIDGET_ENDPOINTS.MODEL_CRUD}`, { headers });
        return response.data;
    } catch (error) {
        console.error('Error getting bot configuration:', error);
        throw error;
    }
}

// Generate bot credentials from API
export const generateBotCredentials = async () => {
    try {
        const token = await tokenRefreshService.getValidToken();
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
        const response = await axios.post(`${API_BASE_URL}${WIDGET_ENDPOINTS.BOT_CREDS}`, {}, { headers });
        return response.data;
    } catch (error) {
        console.error('Error generating bot credentials:', error);
        throw error;
    }
}

// Create or update bot configuration (single POST endpoint)
export const saveBotConfiguration = async (configData) => {
    try {
        const token = await tokenRefreshService.getValidToken();
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }

        // Map frontend config to API format
        const apiData = mapConfigToApiData(configData);

        const response = await axios.post(`${API_BASE_URL}${WIDGET_ENDPOINTS.MODEL_CRUD}`, apiData, { headers });
        return response.data;
    } catch (error) {
        console.error('Error saving bot configuration:', error);
        throw error;
    }
}

// Legacy functions for backward compatibility
export const createBotConfiguration = saveBotConfiguration;
export const updateBotConfiguration = saveBotConfiguration;

// Delete bot configuration
export const deleteBotConfiguration = async () => {
    try {
        const token = await tokenRefreshService.getValidToken();
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
        const response = await axios.delete(`${API_BASE_URL}${WIDGET_ENDPOINTS.MODEL_CRUD}`, { headers });
        return response.data;
    } catch (error) {
        console.error('Error deleting bot configuration:', error);
        throw error;
    }
}

// Upload chatbot avatar image (form-data: file)
export const uploadChatbotAvatarImage = async (file) => {
    try {
        const token = await tokenRefreshService.getValidToken();
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
            `${API_BASE_URL}${WIDGET_ENDPOINTS.CHATBOT_AVATAR_UPLOAD}`,
            formData,
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                    // Important: let the browser set Content-Type with boundary for multipart
                }
            }
        );

        return response.data; // { status, message, avatar_path, avatar_url }
    } catch (error) {
        console.error('Error uploading chatbot avatar image:', error);
        throw error;
    }
}

// Map API response to frontend config format
export const mapApiResponseToConfig = (apiData) => {
    return {
        // Widget appearance settings
        position: apiData.position || 'bottom-right',
        theme: apiData.theme || 'dark',
        themeStyle: apiData.theme_style || 'solid',
        primaryColor: apiData.primary_color || '#3b82f6',
        // Avatar mapping: emoji and image URL from API
        chatbotAvatar: apiData.chatbot_avatar_emoji || '🤖',
        customAvatar: apiData.chatbot_avatar_img || '',
        zIndex: apiData.z_index || 9999,

        // Widget behavior settings
        autoOpen: apiData.auto_open || false,
        showWelcomeMessage: apiData.show_welcome_message || true,
        isActive: typeof apiData.is_active === 'boolean' ? apiData.is_active : true,
        placeholder: apiData.placeholder_text || 'Type your message...',

        // Bot configuration from API
        llmProvider: apiData.llm_provider || 'google',
        modelName: apiData.model_name || 'gemini-2.5-flash-lite',
        // Keys from API (encrypted)
        apiKeyEncrypted: apiData.api_key_encrypted || '',
        botName: apiData.bot_name || 'Talkida Technologies',
        greetingMsg: apiData.greeting_msg || 'Hello! How can I help you today?',
        byeMsg: apiData.bye_msg || 'Bye, have a great day!',
        notFoundInfo: apiData.not_found_info || 'I\'m sorry, I don\'t have that information at the moment.',
        customSystPrompt: apiData.custom_syst_prompt || '',
        maxTokens: apiData.max_tokens || 150,
        temperature: apiData.temperature || 0.0,
        botAim: apiData.bot_aim || '',
        companyName: apiData.company_name || '',

        // Lead collection settings
        enableLeadCollection: apiData.enable_lead_collection || false,
        leadFields: apiData.lead_fields || 'full_name,email_address,phone_number',

        // Additional fields from API
        id: apiData.id || 'default',
        userId: apiData.user_id,
        botUuid: apiData.bot_uuid,
        botSecretKey: apiData.bot_secret_key_encrypted,
        uploadedDocument: apiData.uploaded_document,
        exportedLeadsFile: apiData.exported_leads_file || null,
        name: apiData.bot_name || 'Default Widget',
        botId: apiData.bot_uuid || `bot-${apiData.id}`,
        botToken: apiData.bot_secret_key_encrypted || `token-${apiData.id}`,
        createdAt: apiData.created_at || new Date().toISOString(),
        updatedAt: apiData.updated_at || new Date().toISOString()
    };
}

// Map frontend config to API format
export const mapConfigToApiData = (config) => {
    return {
        llm_provider: config.llmProvider || 'google',
        model_name: config.modelName || 'gemini-2.5-flash-lite',
        // Latest API expects encrypted key field name
        api_key_encrypted: config.apiKeyEncrypted || '',
        bot_aim: config.botAim || '',
        company_name: config.companyName || '',
        bot_name: config.botName || 'Talkida Technologies',
        max_tokens: config.maxTokens || 150,
        greeting_msg: config.greetingMsg || 'Hello! How can I help you today?',
        bye_msg: config.byeMsg || 'Bye, have a great day!',
        not_found_info: config.notFoundInfo || 'I\'m sorry, I don\'t have that information at the moment.',
        custom_syst_prompt: config.customSystPrompt || '',
        temperature: config.temperature || 0.0,

        // Widget appearance settings
        position: config.position || 'bottom-right',
        theme: config.theme || 'dark',
        theme_style: config.themeStyle || 'solid',
        primary_color: config.primaryColor || '#3b82f6',
        // Send separate emoji and image fields per latest API
        chatbot_avatar_emoji: config.chatbotAvatar || '🤖',
        chatbot_avatar_img: config.customAvatar || '',
        z_index: config.zIndex || 9999,

        // Widget behavior settings
        auto_open: config.autoOpen || false,
        show_welcome_message: config.showWelcomeMessage || true,
        is_active: typeof config.isActive === 'boolean' ? config.isActive : true,
        placeholder_text: config.placeholder || 'Type your message...',

        // Lead collection settings
        enable_lead_collection: config.enableLeadCollection || false,
        lead_fields: config.leadFields || 'full_name,email_address,phone_number',

        // Document upload
        uploaded_document: config.uploadedDocument || null,
        exported_leads_file: config.exportedLeadsFile || null,

        // Identifiers if present (server may ignore on create, use for upsert)
        bot_uuid: config.botUuid || undefined,
        bot_secret_key_encrypted: config.botSecretKey || undefined
    };
}